import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import axios from "../../api/axios";
import toast from "../../components/sweetalert/Sweetalert";
import { destroySession } from "../../helpers/Session";
import { useNavigate } from "react-router-dom";
import { useSidebarData } from "../../hooks/sidebarData";
import { useDtrData } from "../../hooks/dtrData";
import Swal from "sweetalert2";

const MyDTR = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { setSidebarData } = useSidebarData();
    const { setDtrData } = useDtrData();
    
    const serverYear = new Date().getFullYear();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken');
    const uname = sessionStorage.getItem('uname');
    const name = sessionStorage.getItem('name');
    const email = sessionStorage.getItem('email');
    const employeeid = sessionStorage.getItem('employeeid');
    const department = sessionStorage.getItem('department');
    const departmentcode = sessionStorage.getItem('departmentslug');
    const level = sessionStorage.getItem('level') || '';
    const aimtoken = sessionStorage.getItem('aimtoken') || '';

    const [loading, setLoading] = useState(false);
    const [selectedYear, setSelectedYear] = useState(null);
    const [currentPayrollDate, setCurrentPayrollDate] = useState('');
    const [currentPayperiod, setCurrentPayperiod] = useState(null);
    const [activeDTRCell, setActiveDTRCell] = useState(null);

    const { data: years, isLoading: isLoadingYears } = useQuery({
        queryKey: ['payperiodyears'],
        queryFn: async () => {
            const res = await axios.get('payperiods/years', {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        staleTime: Infinity
    });

    const currentYear = selectedYear || (years && years[0]?.year);
    const currentYearToDisplay = parseInt(currentYear) || new Date().getFullYear();

    const { data: payperiods, isLoading: isLoadingPayperiods, error: payperiodsError } = useQuery({
        queryKey: ['payperiodlists', currentYear],
        queryFn: async () => {
            const res = await axios.get(`payperiods/years/${currentYear}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` },
            });
            return res.data.response;
        },
        enabled: !!currentYear,
        staleTime: Infinity
    });

    useEffect(() => {
        if (payperiodsError) {
            const errorMessage = payperiodsError.response?.status === 400 
                ? 'Bad Request: Please check your input.' 
                : 'Something went wrong while fetching pay periods.';
            toast.fire({ icon: 'error', title: errorMessage });
        }
    }, [payperiodsError]);

    useEffect(() => {
        if (serverYear === currentYearToDisplay) {
            setCurrentPayperiod(0);
            setCurrentPayrollDate('');
        } else {
            if (payperiods && payperiods.length > 0) {
                setDtrData('payperiod', payperiods[0]?.id);
                setCurrentPayperiod(payperiods[0]?.id);
                setCurrentPayrollDate(payperiods[0]?.payroll);
            }
        }
    }, [payperiods, serverYear, currentYearToDisplay]);

    const resetSidebarData = () => {
        setSidebarData('state', false);
        setSidebarData('key', '');
        setSidebarData('label', '');
        setSidebarData('data', []);
        setDtrData('payperiod', '');
        setActiveDTRCell(null);
    };

    const handleYearSelection = (e) => {
        setSelectedYear(e.target.value);
        resetSidebarData();
    };

    const handlePayperiodData = (e) => {
        const selectedOption = e.target.selectedOptions[0];
        setCurrentPayperiod(selectedOption.value);
        setCurrentPayrollDate(selectedOption.getAttribute('data-payroll'));
        resetSidebarData();
    };

    const { data: dtr, isLoading: isLoadingDTR, error: dtrError } = useQuery({
        queryKey: ['userDTR', { uname, currentPayperiod }],
        queryFn: async () => {
            const res = await axios.get(`dtr/users/${uname}?payperiod=${currentPayperiod}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` },
            });
            return res.data.response;
        },
        enabled: currentPayperiod !== null && currentPayperiod !== undefined,
        retry: 1,
    });

    useEffect(() => {
        if (dtrError) {
            toast.fire({
                icon: 'error',
                title: dtrError.response?.data?.response || 'Something went wrong while fetching DTR!'
            });

            if (dtrError.response?.data?.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    }, [dtrError]);

    const { data: dtrStatus, isLoading: isLoadingDTRStatus, error: dtrStatusError } = useQuery({
        queryKey: ['dtrstatus', { uname, currentPayperiod }],
        queryFn: async () => {
            const res = await axios.get(`dtr/status?uname=${uname}&payperiod=${currentPayperiod}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` },
            });
            return res.data.response;
        },
        enabled: !!currentPayperiod,
    });

    useEffect(() => {
        if (dtrStatusError) {
            if (dtrStatusError.response?.data?.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    }, [dtrStatusError]);

    const submitDTRMutation = useMutation({
        mutationFn: async (timesheetData) => {
            const res = await axios.post(`dtr/users/${uname}`, timesheetData, {
                headers: { 'Authorization': `Bearer ${accesstoken}`, 'X-Aim-Token': aimtoken, }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            setCurrentPayperiod(currentPayperiod);
            queryClient.invalidateQueries(['dtrstatus', { uname, currentPayperiod }]);
            setLoading(false);
            toast.fire({ icon: 'success', title: data });
        },
        onError: (error) => {
            setLoading(false);
            toast.fire({
                icon: 'error',
                title: error.response?.data?.response || 'Something went wrong while submitting timesheet record!'
            });

            if (error.response?.data?.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleSubmitDTR = () => {
        Swal.fire({
            text: 'Are you sure you want to send this timesheet for approval?',
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: (result) => {
                return new Promise((resolve) => {
                    if (result) {
                        setLoading(true);
                        submitDTRMutation.mutate({
                            payperiod: currentPayperiod,
                            name,
                            email,
                            employeeid,
                            department,
                            departmentcode,
                            level
                        });
                        resolve();
                    }
                });
            }
        });
    };

    const handleAdjustmentRequest = (id) => {
        setSidebarData('state', true);
        setSidebarData('key', 'record-adjustment');
        setSidebarData('label', 'Record Adjustment');
        setDtrData('payperiod', currentPayperiod);
        setActiveDTRCell(id);

        const cachedItems = queryClient.getQueryData(['userDTR', { uname, currentPayperiod }]);
        if (cachedItems) {
            const item = cachedItems.find(i => i.id === id);
            if (item) {
                setSidebarData('data', item);
            }
        }
    };

    const handleTimesheetApprovalStatus = () => {
        setSidebarData('state', true);
        setSidebarData('key', 'dtr-approval');
        setSidebarData('label', 'Timesheet Approval Status');
        setDtrData('employee', uname);
        setDtrData('payperiod', currentPayperiod);
    }

    return (
        <div className='timesheet my-dtr'>
            <div className='content'>
                <table className={isLoadingYears || isLoadingPayperiods || isLoadingDTR || loading ? 'loading' : ''}>
                    <thead>
                        <tr>
                            <th colSpan="12" onClick={handleTimesheetApprovalStatus}>TIMESHEET</th>
                        </tr>
                        <tr>
                            <td colSpan="12" className="status">
                                {isLoadingDTRStatus 
                                    ? '' : (
                                        dtrStatus === null 
                                        ? (
                                            <button type="button" className={loading ? 'hide' : ''} onClick={handleSubmitDTR}>
                                                SEND <i className="fa fa-paper-plane"></i>
                                            </button>
                                        ) : (dtrStatus === 0 || dtrStatus === 1 
                                            ? (
                                                <h3 className={`pending ${loading ? 'hide' : ''}`}>Pending</h3>
                                            ) : (dtrStatus === 2 
                                                ? (
                                                    <h3 className={`approved ${loading ? 'hide' : ''}`}>Approved</h3>
                                                ) : (dtrStatus === 3 
                                                    ? (
                                                        <div>
                                                            <button type="button" className={loading ? 'hide' : ''} onClick={handleSubmitDTR}>
                                                                SEND <i className="fa fa-paper-plane"></i>
                                                            </button>
                                                            <h3 className={`disapproved ${loading ? 'hide' : ''}`}>Disapproved</h3>
                                                        </div>
                                                    ) : (null)
                                                )
                                            )
                                        )
                                )}
                            </td>
                        </tr>
                        <tr>
                            <th colSpan="2">PayPeriod</th>
                            <th colSpan="2">
                                <select onChange={handlePayperiodData}>
                                    {serverYear === currentYearToDisplay && (
                                        <option value="0"></option>
                                    )}
                                    {payperiods?.map((item) => (
                                        <option key={item.id} value={item.id} data-payroll={item.payroll}>
                                            {item.sdate} - {item.edate}
                                        </option>
                                    ))}
                                </select>
                            </th>
                            <th colSpan="2">Payroll Date</th>
                            <th colSpan="2">{currentPayrollDate}</th>
                            <th colSpan="2">Payroll Year</th>
                            <th colSpan="2">
                                <select onChange={handleYearSelection}>
                                    {years?.map((item) => (
                                        <option key={item.id} value={item.year}>
                                            {item.year}
                                        </option>
                                    ))}
                                </select>
                            </th>
                        </tr>
                        <tr>
                            <th colSpan="3">Employee ID</th>
                            <th colSpan="3">{employeeid}</th>
                            <th colSpan="3">Company</th>
                            <th colSpan="3">OCVI-MSW</th>
                        </tr>
                        <tr>
                            <th colSpan="3" rowSpan="2">Employee Name</th>
                            <th colSpan="3" rowSpan="2">{name}</th>
                            <th colSpan="3">Department</th>
                            <th colSpan="3">{department}</th>
                        </tr>
                        <tr>
                            <th colSpan="3">Unit / Group</th>
                            <th colSpan="3">Operations</th>
                        </tr>
                        <tr>
                            <th>Day</th>
                            <th>Shift Schedule</th>
                            <th>Time In</th>
                            <th>Time Out</th>
                            <th>Start Break</th>
                            <th>Stop Break</th>
                            <th>Total Break</th>
                            <th>Total Hours</th>
                            <th>Undertime</th>
                            <th>Overbreak</th>
                            <th>ND</th>
                            <th>OT</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoadingDTR ? (
                            [...Array(10)].map((_, i) => (
                                <tr key={i}>
                                    {Array.from({ length: 12 }).map((_, j) => <td key={j}><span></span></td>)}
                                </tr>
                            ))
                        ) : (
                            dtr?.map((item) => (
                                <tr key={item.id} className={item.class + (activeDTRCell === item.id ? ' active' : '')} onClick={() => handleAdjustmentRequest(item.id)}>
                                    <td><span>{item.day}</span></td>
                                    <td><span>{item.shift}</span></td>
                                    <td><span>{item.timein}</span></td>
                                    <td><span>{item.timeout}</span></td>
                                    <td><span>{item.startbreak}</span></td>
                                    <td><span>{item.stopbreak}</span></td>
                                    <td><span>{item.totalbreak}</span></td>
                                    <td><span>{item.totalhours}</span></td>
                                    <td><span>{item.undertime}</span></td>
                                    <td><span>{item.overbreak}</span></td>
                                    <td><span>{item.nd}</span></td>
                                    <td><span>{item.ot}</span></td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MyDTR;
